'use client';

import { useMemo, type ReactNode } from 'react';
import { Button, CopyButton, OTPField } from '@invisionag/components';
import { Trans, useTranslation } from '@invisionag/next/i18n';
import { Result } from '@invisionag/next/result';
import { Step1 } from './step-1';
import { Step2 } from './step-2';
import { Step3 } from './step-3';
import { DownloadLinks } from './download-links';
import { QrCode } from './qr-code';
import { StepSection } from './step-section';
import { Form, FormMessage, SubmitButton } from '@/lib/form';
import * as Cortex from '@/repositories/cortex';
import { unreachable } from '@/lib/exhaustive';

const QRCodeSize = 131;
const keyPrefix = 'login.setup-2fa-form.verification';

export function VerificationForm({
  provisioningUri,
  secret,
  onSuccess,
  onCancel,
  onTimeout,
}: {
  provisioningUri: string;
  secret: string;
  onSuccess: () => void;
  onCancel: () => void;
  onTimeout: (message: string) => void;
}) {
  const { t, i18n } = useTranslation();

  async function verify2fa(formData: FormData) {
    const otp = assertString(formData.get('otp'));

    if (!otp) {
      return Result.Err({
        validationErrors: {
          otp: t(`${keyPrefix}.field.otp.validation.valueMissing`),
        },
      });
    }

    const result = await Cortex.verify2fa(otp);

    if (!result.ok) {
      switch (result.err) {
        case 'invalid-otp':
          return Result.Err({
            message: t(`${keyPrefix}.error.${result.err}`),
          });
        case 'timeout':
          onTimeout(t(`${keyPrefix}.error.${result.err}`));
          return Result.Err({});
        default:
          unreachable('error', result.err);
      }
    }

    onSuccess();
    return Result.Ok();
  }

  return (
    <Form action={verify2fa} className="grid gap-6">
      <div className="grid gap-6 md:grid-cols-[auto_1fr]">
        <StepSection
          icon={<Step1 className="hidden md:block" />}
          title={t(`${keyPrefix}.step-1.heading`)}
        >
          <div>
            <Trans
              i18n={i18n}
              i18nKey={`${keyPrefix}.step-1.apps`}
              components={{ bold: <strong className="font-bold" /> }}
            />
          </div>
          <DownloadLinks />
        </StepSection>

        <StepSection
          icon={<Step2 className="hidden md:block" />}
          title={t(`${keyPrefix}.step-2.heading`)}
        >
          <div className="flex flex-col items-center gap-4 md:flex-row">
            <QrPanel
              label={t(`${keyPrefix}.step-2.qr-code`)}
              provisioningUri={provisioningUri}
            />
            <OrDivider>{t(`${keyPrefix}.step-2.or`)}</OrDivider>
            <SetupKeyPanel
              label={t(`${keyPrefix}.step-2.setup-key`)}
              secret={secret}
            />
          </div>
        </StepSection>

        <StepSection
          icon={<Step3 className="hidden md:block" />}
          title={t(`${keyPrefix}.step-3.heading`)}
        >
          <OTPField
            isRequired
            name="otp"
            label={t(`${keyPrefix}.field.otp.label`)}
            autoFocus
          />
        </StepSection>
      </div>
      <FormMessage />
      <div className="flex gap-2">
        <SubmitButton className="self-start">
          {t(`${keyPrefix}.submit`)}
        </SubmitButton>
        <Button onPress={onCancel}>{t(`${keyPrefix}.cancel`)}</Button>
      </div>
    </Form>
  );
}

function QrPanel({
  label,
  provisioningUri,
}: {
  label: string;
  provisioningUri: string;
}) {
  return (
    <div className="flex flex-col">
      {label}
      <QrCode width={QRCodeSize} margin={0}>
        {provisioningUri}
      </QrCode>
    </div>
  );
}

function OrDivider({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center self-stretch md:flex-col">
      <div className="h-px w-auto flex-1 bg-neutral-darker md:h-auto md:w-px" />
      {children}
      <div className="h-px w-auto flex-1 bg-neutral-darker md:h-auto md:w-px" />
    </div>
  );
}

function SetupKeyPanel({ label, secret }: { label: string; secret: string }) {
  const { t } = useTranslation();
  const secretLines = useMemo(() => {
    const middle = secret.length / 2;
    return [
      secret.substring(0, middle + 1).trim(),
      secret.substring(middle + 1).trim(),
    ];
  }, [secret]);

  return (
    <div className="flex flex-col">
      {label}
      <div
        className={`
          flex h-[131px] max-w-[300px] min-w-[131px] flex-col items-center gap-4
          bg-neutral p-4 py-2 pr-4 pl-4
        `}
      >
        <div className="max-w-full justify-center font-monospace">
          <div>{secretLines[0]}</div>
          <div>{secretLines[1]}</div>
        </div>
        <CopyButton value={secret} label={t('common.copy')} />
      </div>
    </div>
  );
}

function assertString(value: unknown): string {
  if (typeof value === 'string') return value;
  throw new Error(`Expected string, got '${typeof value}'`);
}
